/* eslint-disable no-nested-ternary */
// @mui
import { useEffect, useState } from 'react';
import { Theme, SxProps, alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';

// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { accountApiService } from 'src/api/account-api';
import ProfileChangePasswordDialog from 'src/sections/taikhoan/profile-change-password-dialog';
import ProfileInfoDialog from 'src/sections/taikhoan/profile-info-dialog';
import { useConnectDevnet } from './hooks/connect-devnet';
import NotificationsPopover from './notifications-popover';

// ----------------------------------------------------------------------

const loginPaths: Record<string, string> = {
  jwt: paths.auth.jwt.login,
  auth0: paths.auth.auth0.login,
  firebase: paths.auth.firebase.login,
};

// --- HELPER RÚT GỌN ĐỊA CHỈ VÍ ---
const shortenAddress = (address: string, chars = 4) => {
  if (!address) return '';
  return `${address.substring(0, chars)}...${address.substring(address.length - chars)}`;
};

// --- COMPONENT POPUP LIÊN KẾT VÍ ---
type WalletConnectDialogProps = {
  open: boolean;
  onClose: () => void;
};

function WalletConnectDialog({ open, onClose }: WalletConnectDialogProps) {
  const { connect, walletAddress, loading: connecting, errorMsg } = useConnectDevnet();
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const syncWalletToServer = async () => {
      if (walletAddress && !isSyncing) {
        try {
          setIsSyncing(true);
          await accountApiService.connectionWallet(walletAddress);
          alert('Liên kết ví thành công!');
          onClose();  
        } catch (error) {
          console.error('Lỗi sync API:', error);
          alert('Lỗi khi lưu ví vào hệ thống.');
        } finally {
          setIsSyncing(false);
        }
      }
    };
    syncWalletToServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress]);

  const isLoading = connecting || isSyncing;

  return (
    <Dialog 
      open={open} onClose={onClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 2, backgroundImage: 'none', overflow: 'visible' } }}
    >
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary', zIndex: 9 }}>
        <Iconify icon="eva:close-fill" />
      </IconButton>
      <DialogContent sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
           <Iconify icon="fluent:wallet-credit-card-24-filled" width={40} />
        </Box>
        <Typography variant="h5" paragraph sx={{ fontWeight: 700 }}>Kết nối Ví</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>Kết nối ví Phantom để đồng bộ.</Typography>
        <Button
          fullWidth size="large" variant="contained" onClick={connect} disabled={isLoading}
          sx={{ py: 1.5, bgcolor: '#212B36', color: '#fff', borderRadius: 1.5, fontSize: '1rem', '&:hover': { bgcolor: '#454F5B' } }}
        >
          {isLoading ? (isSyncing ? 'Đang đồng bộ...' : 'Đang kết nối...') : 'Kết nối ngay'}
        </Button>
        {errorMsg && <Box sx={{ mt: 2, color: 'error.main' }}>{errorMsg}</Box>}
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

type Props = { sx?: SxProps<Theme> };

export default function UserMenuButton({ sx }: Props) {
  const { user, method, logout} = useAuthContext();
  
  // 1. Dữ liệu từ DB
  const dbWalletAddress = user?.wallet?.diaChiVi;
  const isLinkedWallet = !!dbWalletAddress;
  
  // 2. State cho Extension
  const [isPhantomInstalled, setIsPhantomInstalled] = useState(false);
  const [currentExtAddress, setCurrentExtAddress] = useState<string | null>(null);

  useEffect(() => {
    const checkPhantomStatus = async () => {
      const solana = (window as any).solana;
      const isInstalled = solana?.isPhantom || false;
      setIsPhantomInstalled(isInstalled);

      if (isInstalled) {
        try {
          const resp = await solana.connect({ onlyIfTrusted: true });
          if (resp?.publicKey) {
            setCurrentExtAddress(resp.publicKey.toString());
          }
        } catch (err) {
          // Chưa trust hoặc chưa login extension
          setCurrentExtAddress(null);
        }
      }
    };

    checkPhantomStatus();
    // Lắng nghe sự kiện thay đổi account trên ví Phantom
    if ((window as any).solana) {
        (window as any).solana.on('accountChanged', (publicKey: any) => {
            if (publicKey) {
                setCurrentExtAddress(publicKey.toString());
            } else {
                setCurrentExtAddress(null); // Disconnected/Locked
            }
        });
    }
  }, []);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openWalletDialog, setOpenWalletDialog] = useState(false);
  const openProfileInfo = useBoolean();
  const openChangePass = useBoolean();
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleOpenWallet = () => {
    handleClose();
    
    // --- UPDATED LOGIC START ---
    // Nếu đã có địa chỉ ví trong DB thì KHÔNG mở popup
    if (isLinkedWallet) {
        return; 
    }
    // --- UPDATED LOGIC END ---

    // Nếu chưa có trong DB và chưa cài Extension -> Mở link tải
    if (!isPhantomInstalled) {
       window.open('https://phantom.app/download', '_blank');
       return;
    }
    // Các trường hợp còn lại mở Dialog để connect/re-connect
    setOpenWalletDialog(true);
  };

  const handleOpenProfileInfo = () => { handleClose(); openProfileInfo.onTrue(); };
  const handleOpenChangePass = () => { handleClose(); openChangePass.onTrue(); };

  const handleLogout = async () => {
    try { await logout(); handleClose(); } catch (error) { console.error('Logout error:', error); }
  };

  if (!user) {
    const loginPath = loginPaths[method];
    return (
      <Button component={RouterLink} href={loginPath} variant="outlined" size="small" sx={{ mr: 1, minWidth: 'auto', px: 2, fontSize: '0.875rem', ...sx }}>
        <Typography variant="body2">Đăng nhập</Typography>
      </Button>
    );
  }

  const isAdmin = user?.maVaiTros.find((x: { maVaiTro: string }) => x.maVaiTro === 'nft_admin' || x.maVaiTro === 'nft_garden');
  const userName = user.ten || user.tenTaiKhoan || 'Người dùng';
  const userEmail = user.email || '';

  // --- LOGIC XỬ LÝ 4 TRƯỜNG HỢP ---
  
  let menuText = "";
  let menuIcon = "";
  let menuColor = "text.primary";
  let menuIconColor = "inherit";
  let subText = "";

  if (!isLinkedWallet) {
    // === CHƯA LIÊN KẾT VÍ (DB NULL) ===
    if (!isPhantomInstalled) {
      menuText = "Chưa cài Phantom";
      menuIcon = "fluent:wallet-error-24-regular";
      menuColor = "error.main";
      menuIconColor = "error.main";
    } else {
      menuText = "Liên kết ví";
      menuIcon = "eva:credit-card-outline";
      menuColor = "text.primary";
    }
  } else {
    // === ĐÃ CÓ ĐỊA CHỈ TRONG DB ===
    const shortDbAddress = shortenAddress(dbWalletAddress);

    if (!isPhantomInstalled) {
      // Case 3: Có địa chỉ DB nhưng chưa cài Ext trên máy này
      menuText = shortDbAddress;
      subText = "(Thiếu Extension)";
      menuIcon = "eva:alert-triangle-fill";
      menuColor = "warning.main";
      menuIconColor = "warning.main";
    } else {
      // Đã cài Ext -> Check trùng khớp
      // eslint-disable-next-line no-lonely-if
      if (!currentExtAddress) {
         menuText = shortDbAddress;
         subText = "(Vui lòng kết nối)";
         menuIcon = "eva:link-2-outline"; 
         menuColor = "text.secondary";
      } else if (currentExtAddress !== dbWalletAddress) {
         menuText = "Sai ví kết nối"; 
         subText = `Địa chỉ kết nối không hợp lệ`;
         menuIcon = "eva:close-circle-fill";
         menuColor = "error.main";
         menuIconColor = "error.main";
      } else {
         // Case 4.2: Có địa chỉ DB, có Ext, TRÙNG nhau (Success)
         menuText = shortDbAddress; 
         menuIcon = "eva:checkmark-circle-2-fill";
         menuColor = "primary.main";
         menuIconColor = "primary.main";
      }
    }
  }

  return (
    <>
      <Button
        onClick={handleClick} variant="outlined" size="small"
        sx={{ mr: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 'auto', px: 1.5, ...sx }}
      >
        <Avatar sx={{ width: 24, height: 24, bgcolor: isAdmin ? 'primary.main' : 'secondary.main', fontSize: '0.875rem' }}>
          {userName.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.875rem' }}>
          {userName}
        </Typography>
        <Iconify icon="eva:arrow-ios-downward-fill" width={16} />
      </Button>

      <Menu
        anchorEl={anchorEl} open={open} onClose={handleClose}
        PaperProps={{ sx: { width: 300, mt: 1, maxHeight: '80vh', overflow: 'auto' } }} 
        transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <ListItemIcon><Avatar sx={{ width: 32, height: 32 }}>{userName.charAt(0).toUpperCase()}</Avatar></ListItemIcon>
          <ListItemText primary={userName} secondary={userEmail} primaryTypographyProps={{ variant: 'subtitle2' }} secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
        </MenuItem>
        <Divider />
        
        {isAdmin && (
          <MenuItem component={RouterLink} href={paths.dashboard.root} onClick={handleClose}>
              <ListItemIcon><Iconify icon="eva:settings-2-fill" width={20} /></ListItemIcon>
              <ListItemText primary="Quản trị" />
          </MenuItem>
        )}
        
        <MenuItem onClick={handleOpenProfileInfo}>
          <ListItemIcon><Iconify icon="eva:person-outline" width={20} /></ListItemIcon>
          <ListItemText primary="Cập nhật tài khoản" />
        </MenuItem>
        <MenuItem onClick={handleOpenChangePass}>
          <ListItemIcon><Iconify icon="eva:lock-outline" width={20} /></ListItemIcon>
          <ListItemText primary="Đổi mật khẩu" />
        </MenuItem>
        <Divider />

        {/* --- MENU VÍ THÔNG MINH --- */}
        <MenuItem 
            onClick={isLinkedWallet ? undefined : handleOpenWallet}
            sx={{ cursor: isLinkedWallet ? 'default' : 'pointer' }}
        >
          <ListItemIcon>
            <Iconify icon={menuIcon} width={20} sx={{ color: menuIconColor }} /> 
          </ListItemIcon>
          <ListItemText 
            primary={menuText}
            secondary={subText} 
            primaryTypographyProps={{
              color: menuColor,
              fontWeight: (menuColor === 'primary.main' || menuColor === 'error.main') ? 'fontWeightSemiBold' : 'fontWeightRegular',
              fontFamily: isLinkedWallet ? 'monospace' : 'inherit'
            }}
            secondaryTypographyProps={{
                fontSize: '0.75rem',
                color: menuColor === 'error.main' ? 'error.main' : 'text.disabled'
            }}
          />
        </MenuItem>
        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon><Iconify icon="eva:log-out-outline" width={20} /></ListItemIcon>
          <ListItemText primary="Đăng xuất" />
        </MenuItem>
      </Menu>
     
      <WalletConnectDialog open={openWalletDialog} onClose={() => setOpenWalletDialog(false)} />
      <ProfileInfoDialog open={openProfileInfo.value} onClose={openProfileInfo.onFalse} />
      <ProfileChangePasswordDialog open={openChangePass.value} onClose={openChangePass.onFalse} />
      <Box sx={{marginRight:2}}><NotificationsPopover /></Box>
    </>
  );
}